import React, { useMemo, useState, useEffect } from "react";
import { Box, IconButton, Typography, Collapse, CircularProgress, Alert } from "@mui/material";
import {
  Download,
  Sheet,
  X,
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import * as XLSX from "xlsx";
import { getReportColumns, generateReport, getUserReportFields, saveUserReportingConfigurationForVessel } from "../../services/reportService";
import {
  getAllVessels,
} from "../../services/vesselService";
import { toast } from "react-hot-toast";
import CustomSelect from '../../components/Common/CustomSelect';

const ExcelManagement = () => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState('');
  const [selectedVesselName, setSelectedVesselName] = useState('');
  const [vessels, setVessels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [requiredFieldsExpanded, setRequiredFieldsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default fields as fallback
  const defaultFields = useMemo(() => [
    { id: "ID", label: "ID", required: true },
    { id: "Vessel_Name", label: "Vessel Name", required: true },
    { id: "Imo_Number", label: "Imo Number", required: true },
    { id: "Current_report_closing_time_LT", label: "Current Report Closing Time (LT)", required: true },
    { id: "UTC_Correction_Time", label: "UTC Correction Time", required: true },
    { id: "Position_Latitude_Deg", label: "Position Latitude (Deg)", required: true },
    { id: "Position_Longitude_Deg", label: "Position Longitude (Deg)", required: true },
    { id: "Load_Condition", label: "Load Condition", required: true },
    { id: "Next_Port", label: "Next Port", required: true },
    { id: "Current_Port_Name", label: "Current Port Name", required: true },
    { id: "Wind_coming_From_degree_True_Wind_Direction", label: "Wind Direction (True)", required: true },
    { id: "Vessel_Heading_Degree_Absolue_Direction", label: "Vessel Heading (Deg)", required: true },
    { id: "Observed_GPS_Distance_nm", label: "Observed GPS Distance (nm)", required: true },
    { id: "ME_kwh_Counter", label: "ME kWh Counter", required: true },
    { id: "AE1_kWh", label: "AE1 kWh", required: true },
    { id: "AE2_kWh", label: "AE2 kWh", required: false },
  ], []);

  function formatVesselName(name) {
    return name ? name.replace(/\s+/g, '_') : '';
  }

  useEffect(() => {
    const fetchAndTransformFields = async () => {
      try {
        setIsLoading(true);
        
        // Fetch both report columns and user saved fields in parallel
        const [reportColumnsResponse, userFieldsResponse] = await Promise.all([
          getReportColumns(),
          getUserReportFields().catch(() => ({ data: { configurations: [] } })) // Graceful fallback
        ]);
        
        console.log("Report Columns API Response:", reportColumnsResponse);
        console.log("User Fields Response:", userFieldsResponse);
        
        const apiData = reportColumnsResponse.data;
        const userConfigurations = userFieldsResponse.data?.configurations || [];
        
        console.log("Required fields count:", apiData.requiredFields?.length);
        console.log("Optional fields count:", apiData.optionalFields?.length);
        console.log("User configurations count:", userConfigurations.length);
        
        const transformFields = (fields, isRequired) => {
          if (!fields || !Array.isArray(fields)) {
            console.warn(`Fields are not an array:`, fields);
            return [];
          }
          const transformed = fields.map(field => ({
            id: field.replace(/[^a-zA-Z0-9]/g, '_'), 
            label: field,
            required: isRequired
          }));
          console.log(`Transformed ${isRequired ? 'required' : 'optional'} fields:`, transformed);
          return transformed;
        };

        const apiRequiredFields = transformFields(apiData.requiredFields, true);
        const apiOptionalFields = transformFields(apiData.optionalFields, false);
        
        // Extract saved user field labels from all configurations
        const savedUserFieldLabels = new Set();
        userConfigurations.forEach(config => {
          if (config.selectedFields && Array.isArray(config.selectedFields)) {
            config.selectedFields.forEach(field => {
              if (typeof field === 'string') {
                savedUserFieldLabels.add(field);
              } else if (field.label) {
                savedUserFieldLabels.add(field.label);
              }
            });
          }
        });
        
        console.log("Saved user field labels:", Array.from(savedUserFieldLabels));
        
        // Merge saved user fields with required fields
        let mergedSelectedFields = [...apiRequiredFields];
        const remainingAvailableFields = [...apiOptionalFields];
        
        // Find and move saved optional fields to selected
        const savedOptionalFields = remainingAvailableFields.filter(field => 
          savedUserFieldLabels.has(field.label)
        );
        
        // Remove saved fields from available fields
        const finalAvailableFields = remainingAvailableFields.filter(field => 
          !savedUserFieldLabels.has(field.label)
        );
        
        // Add saved optional fields to selected fields
        mergedSelectedFields = [...mergedSelectedFields, ...savedOptionalFields];
        
        console.log("Final selected fields:", mergedSelectedFields.length);
        console.log("Final available fields:", finalAvailableFields.length);
        
        setSelectedFields(mergedSelectedFields);
        setAvailableFields(finalAvailableFields);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch report columns:", err);
        console.log("Using default fields instead");
        
        const defaultRequired = defaultFields.filter(f => f.required);
        const defaultOptional = defaultFields.filter(f => !f.required);
        
        console.log("Default required:", defaultRequired.length);
        console.log("Default optional:", defaultOptional.length);
        
        setSelectedFields(defaultRequired);
        setAvailableFields(defaultOptional);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndTransformFields();
  }, [defaultFields]);

    const fetchVessels = async () => {
    try {
      const res = await getAllVessels();
      if (res.status === "success") {
        setVessels(res.data);
        // If there's a selected vessel in state, ensure it exists in the new vessels list
        if (selectedVessel) {
          const vesselExists = res.data.some(v => v._id === selectedVessel);
          if (!vesselExists) {
            setSelectedVessel('');
            setSelectedVesselName('');
          } else {
            // Update the vessel name in case it changed
            const currentVessel = res.data.find(v => v._id === selectedVessel);
            if (currentVessel) {
              setSelectedVesselName(currentVessel.name);
            }
          }
        }
      } else {
        console.error("Failed to fetch vessels");
        setVessels([]);
        setSelectedVessel('');
        setSelectedVesselName('');
      }
    } catch (error) {
      console.error("Error fetching vessels:", error);
      setVessels([]);
      setSelectedVessel('');
      setSelectedVesselName('');
    }
  };
  
  useEffect(() => {
    fetchVessels();
  }, []);

  // Handle vessel selection change
  const handleVesselChange = (vesselId) => {
    setSelectedVessel(vesselId);
    const selected = vessels.find(v => v._id === vesselId);
    setSelectedVesselName(selected ? selected.name : '');
  };

  const filteredAvailableFields = useMemo(() => 
    availableFields.filter(field =>
      field.label.toLowerCase().includes(searchTerm.toLowerCase())
  , [availableFields, searchTerm]));

  const addField = (fieldId) => {
    const fieldToAdd = availableFields.find(field => field.id === fieldId);
    if (fieldToAdd) {
      setSelectedFields([...selectedFields, fieldToAdd]);
      setAvailableFields(availableFields.filter(field => field.id !== fieldId));
    }
  };

  const removeField = (fieldId) => {
    const fieldToRemove = selectedFields.find(field => field.id === fieldId);
    if (fieldToRemove && !fieldToRemove.required) {
      setSelectedFields(selectedFields.filter(field => field.id !== fieldId));
      setAvailableFields([...availableFields, fieldToRemove]);
    }
  };

  const generateExcel = () => {
    if (selectedFields.length === 0) {
      alert("Please select at least one field");
      return;
    }

    const data = selectedFields.map(field => ({
      [field.id]: `Sample ${field.label}`
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "VesselData");
    XLSX.writeFile(wb, "vessel_report.xlsx");
  };

  const handleGenerateReport = async () => {
    if (selectedFields.length === 0) {
      toast.error("Please select at least one field");
      return;
    }

    if (!selectedVessel) {
      toast.error("Please select a vessel");
      return;
    }

    try {
      setIsLoading(true);
      const vesselObj = vessels.find(v => v._id === selectedVessel);

      // Save configuration first
      try {
        console.log("Saving reporting configuration...");
        await saveUserReportingConfigurationForVessel(vesselObj?._id, selectedFields);
        console.log("Configuration saved successfully");
      } catch (saveErr) {
        console.warn("Failed to save configuration:", saveErr);
        // Don't block report generation if saving fails
        toast.error("Warning: Could not save field selection, but report will be generated");
      }

      const requestData = {
        vesselId: vesselObj?._id,
        imoNumber: vesselObj?.imoNumber,
        vesselName: vesselObj?.name,
        selectedFields: selectedFields.map(field => field.label)
      };

      const formattedVesselName = formatVesselName(selectedVesselName);

      const blob = await generateReport(requestData);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vessel_report_${formattedVesselName}.xlsm`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report generated successfully");
    } catch (err) {
      console.error('Report generation error:', err);
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  };

  const requiredFields = selectedFields.filter(field => field.required);
  const optionalFields = selectedFields.filter(field => !field.required);

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 'calc(100vh - 200px)'
      }}>
        <CircularProgress size={60} thickness={4} sx={{ color: '#27ade3' }} />
      </Box>
    );
  }

  return (
    <Box sx={{}}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="flex items-center gap-2 text-[24px] font-semibold text-slate-800 tracking-tight">
            <Sheet className="w-6 h-6 text-[#27ade3]" strokeWidth={2} />
            Reporting Tool Management
          </h2>
          <p className="text-[14px] text-[#0009]">
            Select fields to include in your report
          </p>
        </div>

        <div className="flex items-center gap-4">
          <CustomSelect
            options={vessels}
            value={selectedVessel}
            onChange={handleVesselChange}
            placeholder="Select Vessel"
            className="min-w-[200px]"
            maxHeight="300px"
            clearable={true}
          />

          {/* <button
            onClick={handleGenerateReport}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1a325d] via-[#225a87] to-[#27ade3] shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 rounded-xl relative overflow-hidden group"
            disabled={isLoading}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            <Download className="w-4 h-4 relative z-10" strokeWidth={2.5} />
            <span className="relative z-10">Generate Excel</span>
          </button> */}
            <button
              onClick={handleGenerateReport}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1a325d] via-[#225a87] to-[#27ade3] shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 rounded-xl relative overflow-hidden group"
              disabled={isLoading}
            >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            {isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Download className="w-4 h-4 relative z-10" strokeWidth={2.5} />
            )}
            <span className="relative z-10">
              {isLoading ? 'Generating...' : 'Generate Report'}
            </span>
          </button>
        </div>
      </div>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-4">
            <Typography variant="subtitle1" fontWeight="medium">
              Available Fields
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filteredAvailableFields.length} fields
            </Typography>
          </div>

          {/* Search - unchanged */}
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search fields..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all"
            />
          </div>

          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
            {filteredAvailableFields.length > 0 ? (
              filteredAvailableFields.map((field) => (
                <div
                  key={field.id}
                  className="flex items-center justify-between p-3 bg-gray-50 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors border border-gray-100"
                  onClick={() => addField(field.id)}
                >
                  <span className="text-sm font-medium text-gray-700">
                    {field.label}
                  </span>
                  <Plus size={16} className="text-blue-500" />
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Typography variant="body2">
                  {searchTerm
                    ? "No matching fields found"
                    : "No available fields"}
                </Typography>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-4">
            <Typography variant="subtitle1" fontWeight="medium">
              Selected Fields
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedFields.length} fields selected
            </Typography>
          </div>

          {selectedFields.length > 0 ? (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              <div className="mb-2">
                <button
                  onClick={() => setRequiredFieldsExpanded(!requiredFieldsExpanded)}
                  className={`flex items-center justify-between w-full p-3 rounded-lg transition-all duration-200 ${
                    requiredFieldsExpanded
                      ? "bg-blue-50 border border-blue-200"
                      : "bg-gray-50 hover:bg-gray-100 border border-transparent"
                  }`}
                >
                  <div className="flex items-center">
                    <span
                      className={`font-medium ${
                        requiredFieldsExpanded ? "text-blue-700" : "text-gray-800"
                      }`}
                    >
                      Required Fields
                    </span>
                    <span
                      className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                        requiredFieldsExpanded
                          ? "bg-blue-100 text-blue-800 font-medium"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {requiredFields.length} fields
                    </span>
                  </div>
                  {requiredFieldsExpanded ? (
                    <ChevronUp
                      size={16}
                      className={`${
                        requiredFieldsExpanded ? "text-blue-500" : "text-gray-500"
                      }`}
                      strokeWidth={2.5}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className={`${
                        requiredFieldsExpanded ? "text-blue-500" : "text-gray-500"
                      }`}
                      strokeWidth={2.5}
                    />
                  )}
                </button>

                <Collapse in={requiredFieldsExpanded}>
                  <div className="mt-2 space-y-2">
                    {requiredFields.map((field) => (
                      <div
                        key={field.id}
                        className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="font-medium text-gray-800">
                              {field.label}
                            </span>
                            <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                              Required
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>

              {optionalFields.length > 0 && (
                <div className="mt-4">
                  <div className="mb-2">
                    <Typography variant="subtitle2" fontWeight="medium">
                      Optional Fields
                    </Typography>
                  </div>
                  <div className="space-y-2">
                    {optionalFields.map((field) => (
                      <div
                        key={field.id}
                        className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors"
                      >
                        <div className="flex-1">
                          <span className="font-medium text-gray-800">
                            {field.label}
                          </span>
                        </div>
                        <IconButton
                          onClick={() => removeField(field.id)}
                          size="small"
                          sx={{
                            color: "gray.500",
                            "&:hover": {
                              color: "red.500",
                              backgroundColor: "rgba(239, 68, 68, 0.1)",
                            },
                          }}
                        >
                          <X size={16} />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="flex justify-center mb-2">
                <div className="p-3 bg-gray-100 rounded-full">
                  <Plus size={24} className="text-gray-400" />
                </div>
              </div>
              <Typography variant="body2">
                Add fields from the left panel to build your report
              </Typography>
            </div>
          )}
        </div>
      </div>
    </Box>
  );
};

export default ExcelManagement;